import {
  ceremonyThemeOptions,
  firstOrThirdPersonOptions,
} from '@/constants/dropdownOptions.js';
import TextInput from '@/components/TextInput.js';
import SelectInput from '@/components/SelectInput.js';
import { useFormik } from 'formik';
import { createOrder } from '@/features/orders/ordering/services/orderService.js';
import { getVideoTypes } from '@/features/systemManagement/videoType/videoTypeService.js';
import { CreateValidationSchema } from '@/utils/validationSchemas.js';
import { videoData, updateVideoData } from '../selectVideoType/SelectVideoType.js';
import { useState, useEffect } from 'react';

import './Forms.scss';
import './CreateOrderForm.scss';
import { VIDEO_TYPES, DIGITAL_HUMAN_TYPES } from '@/constants/constants.js';

export const useCreateOrderForm = ({
  client_id,
  user_id,
  formik_ClientInfo,
}) => {
  return useFormik({
    initialValues: {
      id: client_id || '',
      user_id: user_id || '',
      clientName: '',
      ceremonyTheme: ceremonyThemeOptions[0].value,
      ceremonyDate: '',
      videoType: '',
      videoTypeId: null,
      custom_price: '',
      status: 9,
      firstOrThirdPerson: firstOrThirdPersonOptions[0].value,
      salesPerson: '',
      telephone: '',
    },
    validationSchema: CreateValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      try {
        const response = await createOrder({
          id: values.id,
          user_id: values.user_id,
          clientName: values.clientName,
          ceremonyTheme: values.ceremonyTheme,
          ceremonyDate: values.ceremonyDate,
          status: values.status,
          firstOrThirdPerson: values.firstOrThirdPerson,
          salesPerson: values.salesPerson,
          telephone: values.telephone,
          videoTypeId: values.videoTypeId,
          custom_price: values.custom_price || '',
        });

        // 成功后设置formik_ClientInfo的值
        formik_ClientInfo.setFieldValue('client_id', values.id);
        formik_ClientInfo.setFieldValue('clientName', values.clientName);
        formik_ClientInfo.setFieldValue('ceremonyTheme', values.ceremonyTheme);
        formik_ClientInfo.setFieldValue('videoType', values.videoType);
        formik_ClientInfo.setFieldValue('videoTypeId', values.videoTypeId);
        formik_ClientInfo.setFieldValue('ceremonyDate', values.ceremonyDate);
        formik_ClientInfo.setFieldValue(
          'firstOrThirdPerson',
          values.firstOrThirdPerson
        );
        formik_ClientInfo.setFieldValue('salesPerson', values.salesPerson);
        formik_ClientInfo.setFieldValue('telephone', values.telephone);
        setStatus({ success: true });
        return response;
      } catch (error) {
        setStatus({ success: false, error: error.message || '订单创建失败' });
        setErrors({ submit: error.message || '订单创建失败' });
        throw error;
      } finally {
        setSubmitting(false);
      }
    },
  });
};

const CreateOrderForm = ({ formik }) => {
  const [videoTypeOptions, setVideoTypeOptions] = useState([]);

  const selectedVideoObj = videoData.find((v) => v.title === formik.values.videoType);
  const localHasCustomPrice = selectedVideoObj ? !!selectedVideoObj.has_custom_price : false;

  // 加载视频类型数据
  useEffect(() => {
    const loadVideoTypes = async () => {
      try {
        const types = await getVideoTypes();
        updateVideoData(types);
        const options = types.map((type) => ({
          value: type.title,
          label: type.title,
        }));
        setVideoTypeOptions(options);
      } catch (error) {
        console.error('Failed to load video types:', error);
        setVideoTypeOptions([]);
      }
    };

    loadVideoTypes();
  }, []);

  const handleVideoTypeChange = (e) => {
    const selectedTitle = e.target.value;
    formik.setFieldValue('videoType', selectedTitle);
    const selectedVideo = videoData.find((item) => item.title === selectedTitle);
    formik.setFieldValue('videoTypeId', selectedVideo ? selectedVideo.id : '');
  };

  const getVideoInfo = () => {
    const selectedVideo = videoData.find(
      (item) => item.title === formik.values.videoType
    );
    if (selectedVideo) {
      return (
        <>
          <div className="videoType-description-container">
            <h5>{selectedVideo.title}</h5>
            <div className="description-text">
              {selectedVideo.title !== VIDEO_TYPES.电影级定制 ? (
                <div className="videoCard-description-container">
                  {selectedVideo.video_length && (
                    <div className="no-wrap">
                      视频时长: {selectedVideo.video_length}分钟
                    </div>
                  )}
                  {selectedVideo.total_images && (
                    <div className="no-wrap">
                      图片总数: {selectedVideo.total_images}张
                    </div>
                  )}
                  {selectedVideo.static_photos && (
                    <div className="no-wrap">
                      静态照片数量: {selectedVideo.static_photos}
                    </div>
                  )}
                  {selectedVideo.subtitle_required &&
                    selectedVideo.subtitle_word_count && (
                      <div className="no-wrap">
                        字幕: {selectedVideo.subtitle_word_count}
                      </div>
                    )}{' '}
                  {selectedVideo.voice_reproduction && (
                    <div className="no-wrap ">
                      声音处理:{' '}
                      <span className="hint-text">
                        {selectedVideo.voice_reproduction_type}
                      </span>
                    </div>
                  )}{' '}
                  {selectedVideo.text_word_count && (
                    <div className="no-wrap">
                      文案:{' '}
                      <span className="hint-text">
                        {selectedVideo.text_word_count}
                      </span>
                    </div>
                  )}
                  {selectedVideo.dynamic_photos && (
                    <div className="no-wrap">
                      AI动态生成影像照片数量:{' '}
                      <span className="hint-text">
                        {selectedVideo.dynamic_photos}
                      </span>
                    </div>
                  )}
                  {selectedVideo.ai_person_scene_fusion_time && (
                    <div className="no-wrap">
                      AI人物场景融合视频时长:{' '}
                      <span className="hint-text">
                        {selectedVideo.ai_person_scene_fusion_time}
                      </span>
                      分钟
                    </div>
                  )}
                  {selectedVideo.ai_movie_level_scene_fusion_time && (
                    <div className="no-wrap">
                      AI电影级分镜人物影像视频时长:{' '}
                      <span className="hint-text">
                        {selectedVideo.ai_movie_level_scene_fusion_time}
                      </span>
                      分钟
                    </div>
                  )}
                  {selectedVideo.digital_human_has &&
                    (selectedVideo.digital_human_type === DIGITAL_HUMAN_TYPES.ORIGINAL_VOICE ? (
                      <div className="no-wrap">
                        数字人原声复刻告别：
                        {selectedVideo.digital_human_farewell_time}分钟
                      </div>
                    ) : selectedVideo.digital_human_type === DIGITAL_HUMAN_TYPES.WAVE ? (
                      <div className="no-wrap">
                        数字人挥手告别：
                        {selectedVideo.digital_human_wave_time}分钟
                      </div>
                    ) : (
                      <div className="no-wrap">
                        数字人告别：
                        {selectedVideo.digital_human_type}
                      </div>
                    ))}
                  {/* {selectedVideo.modification_times && (
                <div className="no-wrap">
                  修改次数: {selectedVideo.modification_times}次
                </div>
              )} */}
                </div>
              ) : (
                <div className="videoCard-description-container">
                  <div className="no-wrap">视频时长: 8分钟起</div>
                  <div className="no-wrap">
                    客户素材不限，商业影视级制作内容，依次交付：
                  </div>
                  <div className="no-wrap">分镜稿</div>
                  <div className="no-wrap">分镜demo样片</div>
                  <div className="no-wrap">初稿完整影片</div>
                  <div className="no-wrap">终稿完整影片</div>
                </div>
              )}
            </div>
          </div>
          {selectedVideo.has_custom_price ? (
            <div> </div>
          ) : (
            <div className="user-materials-container">
              <h5>用户准备材料</h5>
              <div className="description-text">
                <strong>逝者照片：</strong>
                <p>
                  {selectedVideo.total_images } (并附上照片描述)
                </p>
                {selectedVideo.dynamic_photos && (
                  <p>
                    <strong style={{ color: 'red' }}>
                      *动态照片推荐使用15年以上的老照片。{' '}
                    </strong>
                  </p>
                )}
                <strong>逝者生平介绍: </strong>
                <p>1份 </p>
                {selectedVideo.voice_reproduction && (
                  <>
                    <strong>音频文件: </strong>
                    <strong style={{ color: 'red' }}>
                      {/* *如没有合适声音，可使用音库音色 */}
                      *如果没有逝者清晰的普通话声音，只做不开口说话的数字人
                    </strong>
                    <p style={{ marginBottom: 0 }}>
                      逝者音频文件：1个 （时长15秒以内，并附上说话内容）
                    </p>
                    <p>讲述者音频文件：1个 （时长15秒以内，并附上说话内容） </p>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <form className="row g-3" style={{ width: '100%' }}>
      <TextInput
        name="clientName"
        label="逝者名字"
        selectedVideo="text"
        value={formik.values.clientName}
        onChange={formik.handleChange}
        placeholder="请输入逝者名字"
        error={formik.errors.clientName}
        touched={formik.touched.clientName}
        className="col-md-5"
      />
      <TextInput
        name="ceremonyDate"
        label="仪式时间"
        type="date"
        value={formik.values.ceremonyDate}
        onChange={formik.handleChange}
        placeholder="请选择日期"
        error={formik.errors.ceremonyDate}
        touched={formik.touched.ceremonyDate}
        className="col-md-2"
      />
            <SelectInput
            name="videoType"
            label="视频标准"
            options={videoTypeOptions}
            value={formik.values.videoType}
            onChange={(e) => {
              const selectedValue = e.target.value;
              const selectedVideoData = videoData.find((v) => v.title === selectedValue);
              formik.setFieldValue('videoType', selectedValue);
              if (selectedVideoData) {
                formik.setFieldValue('videoTypeId', selectedVideoData.id);
              }
            }}
            error={formik.errors.videoType}
            touched={formik.touched.videoType}
            onBlur={formik.handleBlur}
            className="col-md-8"
        required={true}
      />
      {localHasCustomPrice && (
        <TextInput
          name="custom_price"
          label="定制价格"
          type="number"
          value={formik.values.custom_price}
          onChange={formik.handleChange}
          placeholder="请输入价格"
          error={formik.errors.custom_price}
          touched={formik.touched.custom_price}
          onBlur={formik.handleBlur}
          className="col-md-4"
        />
      )}
      <TextInput
        name="telephone"
        label="主事人手机号"
        type="text"
        value={formik.values.telephone}
        onChange={formik.handleChange}
        placeholder="请输入主事人手机号"
        error={formik.errors.telephone}
        touched={formik.touched.telephone}
        onBlur={formik.handleBlur}
        className="col-md-5"
      />
      <TextInput
        name="salesPerson"
        label="业务员"
        type="text"
        value={formik.values.salesPerson}
        onChange={formik.handleChange}
        placeholder="请输入业务员姓名"
        className="col-md-5"
        required={false}
        additionalContent={getVideoInfo()}
      />
    </form>
  );
};

export default CreateOrderForm;
